import React, { useEffect, useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const gradientOptions = [
  { value: 'from-red-600 to-red-700', label: 'Kırmızı', preview: 'bg-gradient-to-r from-red-600 to-red-700' },
  { value: 'from-red-600 via-red-500 to-orange-500', label: 'Kırmızı-Turuncu', preview: 'bg-gradient-to-r from-red-600 via-red-500 to-orange-500' },
  { value: 'from-purple-600 to-purple-700', label: 'Mor', preview: 'bg-gradient-to-r from-purple-600 to-purple-700' },
  { value: 'from-blue-600 to-blue-700', label: 'Mavi', preview: 'bg-gradient-to-r from-blue-600 to-blue-700' },
  { value: 'from-green-600 to-green-700', label: 'Yeşil', preview: 'bg-gradient-to-r from-green-600 to-green-700' },
  { value: 'from-orange-500 to-red-600', label: 'Turuncu-Kırmızı', preview: 'bg-gradient-to-r from-orange-500 to-red-600' },
  { value: 'from-pink-500 to-purple-600', label: 'Pembe-Mor', preview: 'bg-gradient-to-r from-pink-500 to-purple-600' },
  { value: 'from-yellow-500 to-orange-500', label: 'Sarı-Turuncu', preview: 'bg-gradient-to-r from-yellow-500 to-orange-500' },
  { value: 'from-teal-500 to-cyan-500', label: 'Teal-Cyan', preview: 'bg-gradient-to-r from-teal-500 to-cyan-500' },
  { value: 'from-indigo-600 to-blue-600', label: 'Indigo-Mavi', preview: 'bg-gradient-to-r from-indigo-600 to-blue-600' },
  { value: 'from-gray-800 to-gray-900', label: 'Koyu', preview: 'bg-gradient-to-r from-gray-800 to-gray-900' },
];

const FIXED_SUBTEXT = 'YETERKİ SEN ÜRET TÜRKİYE';

const BannerTab = ({ settings, updateSetting, onTranslate, translating }) => {
  const rotatingTexts = [
    settings.top_banner_text || '',
    settings.top_banner_subtext || '',
  ].filter((t) => String(t).trim());
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(true);

  const translationCount = Object.keys(settings.top_banner_translations || {}).length;

  useEffect(() => {
    if (rotatingTexts.length < 2) {
      setPreviewIndex(0);
      setPreviewVisible(true);
      return undefined;
    }
    let fadeTimer;
    const interval = setInterval(() => {
      setPreviewVisible(false);
      fadeTimer = setTimeout(() => {
        setPreviewIndex((i) => (i + 1) % rotatingTexts.length);
        setPreviewVisible(true);
      }, 280);
    }, 4000);
    return () => {
      clearInterval(interval);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [rotatingTexts.length, rotatingTexts[0], rotatingTexts[1]]);

  const currentSlogan =
    rotatingTexts[previewIndex % Math.max(rotatingTexts.length, 1)] || 'YEDİ BÖLGEDEN YEDİ DÜVELE';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Üst Banner Ayarları</CardTitle>
            <CardDescription>
              Slogan kısmı 4 saniyede bir değişir; &quot;{FIXED_SUBTEXT}&quot; sitede sabit kalır
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {translationCount > 0 && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {translationCount} dil çevrili
              </Badge>
            )}
            <Switch
              checked={settings.top_banner_enabled}
              onCheckedChange={(v) => updateSetting('top_banner_enabled', v)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preview */}
        <div className={`bg-gradient-to-r ${settings.top_banner_gradient} text-white py-2.5 px-4 rounded-lg text-center text-sm min-h-[2.5rem] flex items-center justify-center gap-2 flex-wrap`}>
          <span
            className={`font-semibold transition-opacity duration-300 ${
              previewVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {currentSlogan}
          </span>
          <span className="text-white/40">•</span>
          <span className="text-white/90">{FIXED_SUBTEXT}</span>
          {settings.top_banner_link_text ? (
            <>
              <span className="text-white/40">•</span>
              <span className="underline text-white/90 text-xs">
                {settings.top_banner_link_text} →
              </span>
            </>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Sol taraftaki slogan admin yazılarından gelir ve 4 sn&apos;de bir döner. Sağdaki metin sabittir.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Dönen Slogan — Yazı 1 (Türkçe)</Label>
            <Input
              value={settings.top_banner_text || ''}
              onChange={(e) => updateSetting('top_banner_text', e.target.value)}
              placeholder="YEDİ BÖLGEDEN YEDİ DÜVELE"
            />
          </div>
          <div className="space-y-2">
            <Label>Dönen Slogan — Yazı 2 (Türkçe)</Label>
            <Input
              value={settings.top_banner_subtext || ''}
              onChange={(e) => updateSetting('top_banner_subtext', e.target.value)}
              placeholder="İkinci slogan (opsiyonel)"
            />
            <p className="text-xs text-muted-foreground">
              Boş bırakılırsa sadece Yazı 1 gösterilir.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Buton Metni (Türkçe)</Label>
            <Input
              value={settings.top_banner_link_text || ''}
              onChange={(e) => updateSetting('top_banner_link_text', e.target.value)}
              placeholder="Hemen Al"
            />
          </div>
          <div className="space-y-2">
            <Label>Link URL</Label>
            <Input
              value={settings.top_banner_link || ''}
              onChange={(e) => updateSetting('top_banner_link', e.target.value)}
              placeholder="/products?featured=true"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-blue-50 border-blue-100 px-4 py-3 space-y-2">
          <p className="text-sm font-medium text-blue-900">Diğer dillere çevirme</p>
          <p className="text-xs text-blue-800">
            Türkçe metinleri yazdıktan sonra aşağıdaki butona basın. EN, DE, FR, AR ve diğer dillere
            otomatik çevrilir; site dil değişince banner o dilde görünür.
          </p>
          {typeof onTranslate === 'function' && (
            <Button
              type="button"
              variant="secondary"
              onClick={onTranslate}
              disabled={translating || !String(settings.top_banner_text || '').trim()}
              className="gap-2"
            >
              {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
              {translating ? 'Çevriliyor...' : 'Bannerı Diğer Dillere Çevir'}
            </Button>
          )}
        </div>

        <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Sabit metin: <span className="font-medium text-foreground">{FIXED_SUBTEXT}</span>
          {' '}(site dil dosyasından gelir)
        </div>

        <div className="space-y-2">
          <Label>Renk Geçişi</Label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {gradientOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateSetting('top_banner_gradient', opt.value)}
                className={`h-12 rounded-lg ${opt.preview} border-2 transition-all ${
                  settings.top_banner_gradient === opt.value ? 'border-black scale-105' : 'border-transparent'
                }`}
                title={opt.label}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BannerTab;
